import { baseGatewayApiV1 } from '@verantos-dev/app-state-base';
import { CohortSummaryAttributeResult } from '@verantos-dev/types-cohorts';
import { ListResponse, ObjectResponse } from '@verantos-dev/types-common';

export type EventRequest = {
  cohortDsl: string;
  startingCohortNode: string | null;
  projectId: string;
  omopEventTableFilter?: string;
};

export type ComputeEventsRequest = {
  cohortDsl: string;
  startingCohortNode: string | null;
  projectId: string;
};

export type EventSummaryRequest = {
  cohortJsonDsl: string;
  startingCohortNode: string | null;
  projectId: string;
};

type PaginationRequestBody = {
  searchParams?: string;
};

export type EventFilters = {
  startYear: {
    label: string;
    options: number[];
    default: number;
  };
  endYear: {
    label: string;
    options: number[];
    default: number;
  };
};

export type EventSummaryResults = {
  kpiSummary: CohortSummaryAttributeResult[];
  eventsSummary: CohortSummaryAttributeResult[];
  personSummary: CohortSummaryAttributeResult[];
  eventFilters: EventFilters;
};

export type ComputeEventsResponse = {
  eventHash: string;
  status: string;
};

export type ConditionOccurrenceEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  conditionStartDate: string;
  conditionEndDate: string;
};
export type ProcedureOccurrenceEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  procedureDate: string;
};
export type DrugExposureEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  drugExposureStartDate: string;
};
export type ObservationEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  observationDate: string;
};
export type VisitOccurrenceEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  visitStartDate: string;
};
export type MeasurementEvent = {
  personId: string;
  entityId: string;
  visitOccurrenceId: string;
  measurementDate: string;
};

export type ColumnStatsRequest = {
  cohortDsl: string;
  startingCohortNode: string | null;
  projectId: string;
  column: string;
  limit?: number;
  omopEventTableFilter?: string;
};

export type TopValue = {
  value: string | number | null;
  count: number;
  percent: number;
};

export type ColumnStats = {
  presentCount: number;
  presentPercentage: number;
  missingCount: number;
  missingPercentage: number;
  uniqueCount: number;
  topValues?: TopValue[];
  totalTopValues?: number;
};

export type EventTable = {
  table_name: string;
};

const eventsDrillDownApiSlice = baseGatewayApiV1
  .enhanceEndpoints({
    addTagTypes: [
      'eventsDrillDown',
      'eventsDrillDown/tables',
      'eventsDrillDown/summary',
      'eventsDrillDown/columnstats',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getEvents: builder.mutation<
        | ListResponse<
            | ConditionOccurrenceEvent
            | ProcedureOccurrenceEvent
            | DrugExposureEvent
            | ObservationEvent
            | VisitOccurrenceEvent
            | MeasurementEvent
          >
        | ObjectResponse<ComputeEventsResponse>,
        EventRequest & PaginationRequestBody
      >({
        query: ({
          cohortDsl,
          startingCohortNode,
          projectId,
          searchParams,
          omopEventTableFilter,
        }) => ({
          url: `/events${searchParams}`,
          method: 'POST',
          body: {
            cohortDsl,
            startingCohortNode,
            projectId,
            omopEventTableFilter,
          },
        }),
        invalidatesTags: ['eventsDrillDown'],
      }),
      getEventSummary: builder.mutation<
        ObjectResponse<EventSummaryResults> | ObjectResponse<ComputeEventsResponse>,
        EventSummaryRequest
      >({
        query: ({ cohortJsonDsl, startingCohortNode, projectId }) => ({
          url: `/events/summary`,
          method: 'POST',
          body: {
            cohortJsonDsl:
              typeof cohortJsonDsl === 'string' ? JSON.parse(cohortJsonDsl) : cohortJsonDsl,
            startingCohortNode,
            projectId,
          },
        }),
        invalidatesTags: ['eventsDrillDown/summary'],
      }),
      getColumnStats: builder.mutation<ColumnStats | ComputeEventsResponse, ColumnStatsRequest>({
        query: ({
          cohortDsl,
          startingCohortNode,
          projectId,
          column,
          limit,
          omopEventTableFilter,
        }) => ({
          url: `/events/columnstats`,
          method: 'POST',
          body: {
            cohortDsl,
            startingCohortNode,
            projectId,
            column,
            ...(limit !== undefined ? { limit } : {}),
            omopEventTableFilter,
          },
        }),
        invalidatesTags: ['eventsDrillDown/columnstats'],
      }),
      postComputeEvents: builder.mutation<ComputeEventsResponse, ComputeEventsRequest>({
        query: ({ cohortDsl, startingCohortNode, projectId }) => ({
          url: `/compute-events`,
          method: 'POST',
          body: { cohortDsl, startingCohortNode, projectId },
        }),
      }),
      getEventTables: builder.query<ListResponse<EventTable>, EventRequest>({
        query: ({ cohortDsl, startingCohortNode, projectId }) => ({
          url: `/events/tables`,
          method: 'POST',
          body: { cohortDsl, startingCohortNode, projectId },
        }),
        providesTags: ['eventsDrillDown/tables'],
      }),
    }),
  });

export const {
  useGetEventSummaryMutation,
  useGetEventsMutation,
  useGetColumnStatsMutation,
  useGetEventTablesQuery,
  usePostComputeEventsMutation,
} = eventsDrillDownApiSlice;
